import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { API, axios } from "@/api";
import { BottomNav } from "@/components/shared/Navigation";
import { ShareSheet, getShareCopy } from "@/components/shared/ShareSheet";
import {
  ArrowLeft, Gift, Users, Loader2, PartyPopper, Calendar,
  ChevronRight, Eye, Sparkles,
} from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { trackPageView } from "@/lib/track";
import { useTheme } from "@/contexts/ThemeContext";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

export default function PlanBirthdayPage() {
  const navigate = useNavigate();
  const { mode } = useTheme();
  const [friendName, setFriendName] = useState("");
  const [birthdayMonth, setBirthdayMonth] = useState("");
  const [creating, setCreating] = useState(false);
  const [createdPlan, setCreatedPlan] = useState(null);
  const [showShare, setShowShare] = useState(false);
  const [myPlans, setMyPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(true);

  useEffect(() => {
    trackPageView("plan_birthday");
    (async () => {
      try {
        const res = await axios.get(`${API}/birthday-plans`);
        setMyPlans(res.data || []);
      } catch {}
      finally { setLoadingPlans(false); }
    })();
  }, []);

  const handleCreate = async () => {
    if (!friendName.trim() || !birthdayMonth) return;
    setCreating(true);
    try {
      const res = await axios.post(`${API}/birthday-plans`, {
        friend_name: friendName.trim(),
        birthday_month: parseInt(birthdayMonth),
      });
      setCreatedPlan(res.data);
      toast.success(`Plan created! +${res.data.points_earned} pts`);
      // Refresh plans
      const plans = await axios.get(`${API}/birthday-plans`);
      setMyPlans(plans.data || []);
    } catch { toast.error("Failed to create plan"); }
    finally { setCreating(false); }
  };

  const shareUrl = createdPlan ? `${window.location.origin}/birthday-plan/${createdPlan.plan_id}` : "";

  return (
    <div className="min-h-screen pb-24" style={{ backgroundColor: "var(--theme-page-bg, #ffffff)" }} data-testid="plan-birthday-page">
      <div className="sticky top-0 z-30 bg-white/90 backdrop-blur-md border-b border-slate-100 px-4 py-3 flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="p-1.5 hover:bg-slate-100 rounded-lg" data-testid="plan-back-btn">
          <ArrowLeft className="w-5 h-5 text-slate-600" />
        </button>
        <h1 className="text-base font-bold text-slate-800">Plan a Friend's Birthday</h1>
      </div>

      <div className="px-4 pt-4 space-y-5">
        {!createdPlan ? (
          <>
            {/* Create form */}
            <div className="bg-gradient-to-br from-pink-50 to-violet-50 rounded-2xl p-5 border border-pink-200">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-pink-100 rounded-xl flex items-center justify-center">
                  <PartyPopper className="w-6 h-6 text-pink-500" />
                </div>
                <div>
                  <h2 className="text-base font-bold text-slate-800">Create a Birthday Plan</h2>
                  <p className="text-xs text-slate-500">Curate the best birthday deals for your friend</p>
                </div>
              </div>

              <div className="space-y-3">
                <div>
                  <label className="text-xs font-semibold text-slate-600 block mb-1.5">Friend's Name</label>
                  <input
                    type="text"
                    value={friendName}
                    onChange={(e) => setFriendName(e.target.value)}
                    placeholder="e.g., Sarah"
                    className="w-full text-sm border border-slate-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-pink-200 bg-white"
                    data-testid="friend-name-input"
                  />
                </div>

                <div>
                  <label className="text-xs font-semibold text-slate-600 block mb-1.5">Birthday Month</label>
                  <select
                    value={birthdayMonth}
                    onChange={(e) => setBirthdayMonth(e.target.value)}
                    className="w-full text-sm border border-slate-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-pink-200 bg-white"
                    data-testid="birthday-month-select"
                  >
                    <option value="">Select month...</option>
                    {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
                  </select>
                </div>

                <button
                  onClick={handleCreate}
                  disabled={creating || !friendName.trim() || !birthdayMonth}
                  className="w-full py-3 text-sm font-semibold rounded-xl bg-pink-500 text-white hover:bg-pink-600 disabled:opacity-40 flex items-center justify-center gap-2"
                  data-testid="create-plan-btn"
                >
                  {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                  {creating ? "Creating..." : "Generate Birthday Plan (+10 pts)"}
                </button>
              </div>
            </div>

            {/* My Plans */}
            {!loadingPlans && myPlans.length > 0 && (
              <div>
                <h3 className="text-sm font-bold text-slate-700 mb-2">Your Plans</h3>
                <div className="space-y-2">
                  {myPlans.map((plan) => (
                    <motion.div
                      key={plan.id}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => navigate(`/birthday-plan/${plan.id}`)}
                      className="bg-white rounded-xl border border-slate-100 p-3.5 shadow-sm cursor-pointer"
                      data-testid={`plan-card-${plan.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2.5">
                          <div className="w-9 h-9 bg-pink-50 rounded-lg flex items-center justify-center">
                            <Gift className="w-4.5 h-4.5 text-pink-500" />
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-slate-800">{plan.friend_name}'s Plan</p>
                            <p className="text-[10px] text-slate-400">
                              {plan.offer_count} offers · ${plan.total_value} total · {MONTHS[plan.birthday_month - 1]}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {plan.views > 0 && (
                            <span className="text-[10px] text-slate-400 flex items-center gap-0.5">
                              <Eye className="w-3 h-3" /> {plan.views}
                            </span>
                          )}
                          <ChevronRight className="w-4 h-4 text-slate-300" />
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            )}
          </>
        ) : (
          /* Success — plan created */
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="space-y-4">
            <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-5 border border-emerald-200 text-center">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <PartyPopper className="w-8 h-8 text-emerald-600" />
              </div>
              <h2 className="text-lg font-bold text-slate-800" data-testid="plan-success-heading">
                {createdPlan.friend_name}'s Plan is Ready!
              </h2>
              <p className="text-sm text-slate-500 mt-1">
                {createdPlan.offer_count} deals worth <span className="font-bold text-emerald-600">${createdPlan.total_value}</span>
              </p>
              <p className="text-xs text-slate-400 mt-1">+{createdPlan.points_earned} points earned</p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => navigate(`/birthday-plan/${createdPlan.plan_id}`)}
                className="flex-1 py-3 text-sm font-semibold rounded-xl bg-slate-100 text-slate-700 hover:bg-slate-200"
                data-testid="view-plan-btn"
              >
                View Plan
              </button>
              <button
                onClick={() => setShowShare(true)}
                className="flex-1 py-3 text-sm font-semibold rounded-xl bg-pink-500 text-white hover:bg-pink-600 flex items-center justify-center gap-1.5"
                data-testid="share-plan-btn"
              >
                <Users className="w-4 h-4" /> Share with {createdPlan.friend_name}
              </button>
            </div>

            <button
              onClick={() => { setCreatedPlan(null); setFriendName(""); setBirthdayMonth(""); }}
              className="w-full py-2.5 text-sm font-semibold text-slate-400 hover:text-slate-600"
              data-testid="create-another-btn"
            >
              Create another plan
            </button>
          </motion.div>
        )}
      </div>

      {showShare && (
        <ShareSheet
          isOpen={showShare}
          onClose={() => setShowShare(false)}
          url={shareUrl}
          title={getShareCopy(mode, "itinerary", { friendName: createdPlan?.friend_name, offerCount: createdPlan?.offer_count, totalValue: createdPlan?.total_value }).title}
          description={getShareCopy(mode, "itinerary", { friendName: createdPlan?.friend_name, offerCount: createdPlan?.offer_count, totalValue: createdPlan?.total_value }).description}
          businessId=""
          businessName={`Plan: ${createdPlan?.friend_name}`}
          shareContext="itinerary"
        />
      )}

      <BottomNav />
    </div>
  );
}
